// pages/ChildDashboard.js - Enhanced version with new card design
import React, { useState, useEffect } from 'react';
import { ArrowLeft, Plus, Target, Coins, Star, Gift, Calendar, Eye, EyeOff, Settings, PiggyBank, Clock, BookOpen, Brain, Trophy, Zap, Lock, Leaf } from 'lucide-react';
import { Button, Card } from '../components/ui';
import { GoalSettingModal } from '../components/GoalSettingModal';
import { ViewAllActivityModal } from '../components/ViewAllActivityModal';

export const ChildDashboard = ({ child, family, guardian, onNavigate, onLogout, userType, onAddMoney }) => {
  const [showBalance, setShowBalance] = useState(true);
  const [recentDeposits, setRecentDeposits] = useState([]);
  const [pendingDeposits, setPendingDeposits] = useState([]);
  const [loadingDeposits, setLoadingDeposits] = useState(true);
  const [childData, setChildData] = useState(child);
  const [showGoalModal, setShowGoalModal] = useState(false);

  const API_BASE_URL = 'https://growthsprouts-server.vercel.app';

  // Child data with fallbacks
  const childName = childData?.first_name || childData?.firstName || 'Little Saver';
  const childAge = childData?.age || 8;
  const totalSaved = childData?.total_saved || childData?.totalSaved || 0;
  const savingsGoal = childData?.savings_goal || childData?.savingsGoal || 100;
  const progressPercent = Math.min((totalSaved / savingsGoal) * 100, 100);
  const [showAllActivityModal, setShowAllActivityModal] = useState(false);

  // Calculate total bonus earned from all deposits
  const totalBonusEarned = recentDeposits.reduce((total, deposit) => {
    return total + parseFloat(deposit.bonus_earned || 0);
  }, 0);

  // Color themes for initials - softer, more subtle palette
  const colorThemes = [
    { bg: 'from-slate-400 to-slate-500', text: 'text-white' },
    { bg: 'from-blue-400 to-blue-500', text: 'text-white' },
    { bg: 'from-cyan-400 to-cyan-500', text: 'text-white' },
    { bg: 'from-emerald-400 to-emerald-500', text: 'text-white' },
    { bg: 'from-green-400 to-green-500', text: 'text-white' },
    { bg: 'from-amber-400 to-amber-500', text: 'text-white' },
    { bg: 'from-orange-400 to-orange-500', text: 'text-white' },
    { bg: 'from-rose-400 to-rose-500', text: 'text-white' },
    { bg: 'from-purple-400 to-purple-500', text: 'text-white' },
    { bg: 'from-indigo-400 to-indigo-500', text: 'text-white' },
  ];

  // Get consistent color theme based on child's name
  const getColorTheme = (name) => {
    if (!name) return colorThemes[0];
    const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colorThemes[hash % colorThemes.length];
  };

  const handleSetGoal = () => {
    setShowGoalModal(true);
  };

  const handleGoalSet = (newGoal) => {
    // Update local state
    setChildData(prev => ({
      ...prev,
      savings_goal: newGoal
    }));
  };

  const colorTheme = getColorTheme(childName);

  // Get first letter of name
  const initial = childName ? childName.charAt(0).toUpperCase() : 'U';

  // Dynamic level system
  const getLevel = (amount) => {
    if (amount >= 500) return {
      level: 'Money Master',
      badge: '🏆',
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-100'
    };
    if (amount >= 200) return {
      level: 'Super Saver',
      badge: '⭐',
      color: 'text-purple-600',
      bgColor: 'bg-purple-100'
    };
    if (amount >= 100) return {
      level: 'Smart Saver',
      badge: '🌟',
      color: 'text-blue-600',
      bgColor: 'bg-blue-100'
    };
    if (amount >= 50) return {
      level: 'Growing Saver',
      badge: '✨',
      color: 'text-green-600',
      bgColor: 'bg-green-100'
    };
    if (amount >= 10) return {
      level: 'Learning Saver',
      badge: '🌱',
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-100'
    };
    return {
      level: 'New Saver',
      badge: '🥚',
      color: 'text-gray-600',
      bgColor: 'bg-gray-100'
    };
  };

  const levelInfo = getLevel(totalSaved);

  // Dynamic card border based on progress
  const getCardBorder = () => {
    if (progressPercent >= 100) return 'border-yellow-400 shadow-yellow-100';
    if (progressPercent >= 75) return 'border-purple-400 shadow-purple-100';
    if (progressPercent >= 50) return 'border-blue-400 shadow-blue-100';
    if (progressPercent >= 25) return 'border-green-400 shadow-green-100';
    return 'border-gray-200 shadow-gray-100';
  };

  // Motivational messages based on progress
  const getMotivationalMessage = () => {
    if (progressPercent >= 100) return "Goal achieved! 🎉";
    if (progressPercent >= 90) return "So close to your goal!";
    if (progressPercent >= 75) return "Amazing progress!";
    if (progressPercent >= 50) return "Halfway there!";
    if (progressPercent >= 25) return "Great start!";
    if (totalSaved > 0) return "Keep it up!";
    return "Ready to start saving?";
  };

  // Load deposit data from your existing API endpoints
  useEffect(() => {
    const loadDeposits = async () => {
      if (!child?.id) return;

      try {
        setLoadingDeposits(true);
        console.log('ChildDashboard: Loading deposits for child:', child.id);

        // Load recent deposits (approved/verified ones)
        const recentResponse = await fetch(`${API_BASE_URL}/api/deposits/history/${child.id}?limit=10`);
        const recentResult = await recentResponse.json();

        if (recentResult.success) {
          setRecentDeposits(recentResult.data || []);
          console.log('ChildDashboard: Loaded recent deposits:', recentResult.data?.length || 0);
        }

        // Load all deposits to find pending ones
        const allResponse = await fetch(`${API_BASE_URL}/api/deposits/all/${child.id}`);
        const allResult = await allResponse.json();

        if (allResult.success) {
          const pending = allResult.data.filter(deposit =>
            deposit.status === 'pending' || deposit.status === 'guardian_approved'
          );
          setPendingDeposits(pending || []);
          console.log('ChildDashboard: Loaded pending deposits:', pending?.length || 0);
        }

      } catch (error) {
        console.error('ChildDashboard: Error loading deposits:', error);
      } finally {
        setLoadingDeposits(false);
      }
    };

    loadDeposits();

    // Refresh every 30 seconds to check for updates
    const interval = setInterval(loadDeposits, 30000);
    return () => clearInterval(interval);
  }, [child?.id]);

  const handleRequestMoney = () => {
    onNavigate('addMoney', childData);
  };

  const handleStartLearning = () => {
    onNavigate('financialLiteracyProgram', { child: child, family: family });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-blue-50">
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          {userType === 'child' ? (
            // Child user - only logout option
            <Button
              variant="ghost"
              onClick={onLogout}  // ✅ Now calls the actual logout function
              className="flex items-center gap-2 text-stone-600 hover:text-stone-800"
            >
              <ArrowLeft className="w-5 h-5" />
              Logout
            </Button>

          ) : (
            // Guardian viewing child's garden - can go back to family dashboard
            <Button
              variant="ghost"
              onClick={() => onNavigate('familyDashboard')}
              className="flex items-center gap-2 text-stone-600 hover:text-stone-800"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Family
            </Button>
          )}

          <div className="text-center">
            <div className="flex items-center justify-center gap-2 flex-wrap">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{childName}'s Garden</h1>
              {family?.program && (
                <div className="inline-flex items-center px-2 py-0.5 bg-gradient-to-r from-emerald-500 to-green-600 rounded-full shadow-sm">
                  <Leaf className="w-3 h-3 mr-1 text-white" />
                  <span className="text-xs font-medium text-white">{family.program}</span>
                </div>
              )}
            </div>
            <div className="text-sm text-gray-600">Age {childAge}</div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowBalance(!showBalance)}
              className="text-gray-600 hover:text-gray-700 p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {showBalance ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
            </button>
            <button className="text-gray-600 hover:text-gray-700 p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <Settings className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Enhanced Main Garden Card */}
        <Card className={`p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border-2 ${getCardBorder()} relative overflow-hidden`}>
          {/* Background Pattern */}
          <div className={`absolute inset-0 opacity-5 bg-gradient-to-br ${colorTheme.bg}`} />

          {/* Achievement Confetti for goal achievers */}
          {progressPercent >= 100 && (
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute top-4 left-4 text-yellow-400 animate-bounce">✨</div>
              <div className="absolute top-6 right-6 text-pink-400 animate-pulse">🎉</div>
              <div className="absolute bottom-8 left-6 text-purple-400 animate-bounce delay-300">⭐</div>
            </div>
          )}

          <div className="relative z-10">
            <div className="flex items-center justify-between mb-6">
              {/* Enhanced Avatar & Info */}
              <div className="flex items-center space-x-4">
                <div className={`w-20 h-20 rounded-2xl flex justify-center items-center bg-gradient-to-br ${colorTheme.bg} shadow-lg relative`}>
                  <span className={`text-2xl font-bold ${colorTheme.text} drop-shadow-sm`}>{initial}</span>
                  {/* Level Badge */}
                  <div className={`absolute -top-2 -right-2 w-7 h-7 ${levelInfo.bgColor} rounded-full flex justify-center items-center border-2 border-white text-xs shadow-md`}>
                    <span>{levelInfo.badge}</span>
                  </div>
                </div>
                <div>
                  <div className={`text-xs font-semibold mb-1 px-2 py-1 rounded-full ${levelInfo.bgColor} ${levelInfo.color}`}>
                    {levelInfo.level}
                  </div>
                  <div className="text-lg font-bold text-gray-900">{childName}'s Garden</div>
                  <div className="text-sm text-gray-600">Age {childAge}</div>
                </div>
              </div>

              {/* Enhanced Balance Display */}
              <div className="text-right">
                {showBalance ? (
                  <>
                    <div className={`text-3xl font-bold mb-1 bg-gradient-to-r ${colorTheme.bg} bg-clip-text text-transparent`}>
                      ${totalSaved.toFixed(2)}
                    </div>
                    <div className="text-sm text-gray-600">My Garden Value</div>
                    {totalBonusEarned > 0 && (
                      <div className="text-xs text-emerald-600 font-medium mt-1">
                        Includes ${totalBonusEarned.toFixed(2)} bonus!
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-3xl font-bold text-gray-400">••••</div>
                )}
              </div>
            </div>

            {/* Motivational Message */}
            <div className="text-center mb-4">
              <div className="text-sm text-emerald-600 italic font-medium">
                {getMotivationalMessage()}
              </div>
            </div>

            {/* Enhanced Progress Section */}
            <div className="space-y-3">
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">Goal Progress</span>
                <span className="font-medium text-gray-700">{Math.round(progressPercent)}%</span>
              </div>

              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div
                  className={`h-full rounded-full bg-gradient-to-r ${colorTheme.bg} transition-all duration-500 ease-out`}
                  style={{ width: `${progressPercent}%` }}
                />
              </div>

              <div className="text-xs text-gray-500 text-center">
                ${(savingsGoal - totalSaved).toFixed(2)} to go • Goal: ${savingsGoal.toFixed(2)}
              </div>
            </div>
          </div>
        </Card>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="p-4 hover:shadow-lg transition-shadow cursor-pointer" onClick={handleRequestMoney}>
            <div className="flex items-center space-x-3">
              <div className={`w-12 h-12 bg-gradient-to-br ${colorTheme.bg} rounded-xl flex items-center justify-center`}>
                <Plus className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="font-semibold text-gray-900">Add Money</div>
                <div className="text-sm text-gray-600">Ask parents to add</div>
              </div>
            </div>
          </Card>

          <Card className="p-4 hover:shadow-lg transition-shadow cursor-pointer" onClick={handleSetGoal}>
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-xl flex items-center justify-center">
                <Target className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="font-semibold text-gray-900">New Goal</div>
                <div className="text-sm text-gray-600">Set savings target</div>
              </div>
            </div>
          </Card>

          <Card className="p-4 hover:shadow-lg transition-shadow cursor-pointer" onClick={handleStartLearning}>
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-500 rounded-xl flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="font-semibold text-gray-900">Learn & Earn</div>
                <div className="text-sm text-gray-600">Complete lessons</div>
              </div>
            </div>
          </Card>
        </div>

        {/* Growth Milestones - Compact */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Trophy className="w-5 h-5 text-yellow-500 mr-2" />
            Growth Journey
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              { stage: 'Seed', amount: 0, emoji: '🌰', unlocked: totalSaved >= 0 },
              { stage: 'Sprout', amount: 50, emoji: '🌱', unlocked: totalSaved >= 50 },
              { stage: 'Plant', amount: 100, emoji: '🌿', unlocked: totalSaved >= 100 },
              { stage: 'Tree', amount: 200, emoji: '🌳', unlocked: totalSaved >= 200 },
              { stage: 'Golden', amount: 500, emoji: '👑', unlocked: totalSaved >= 500 },
            ].map((milestone, index) => (
              <div
                key={index}
                className={`text-center p-3 rounded-xl border-2 transition-all ${milestone.unlocked
                    ? 'border-emerald-200 bg-emerald-50 shadow-md'
                    : 'border-gray-200 bg-gray-50 opacity-60'
                  }`}
              >
                <div className={`text-xl mb-1 ${!milestone.unlocked && 'grayscale'}`}>
                  {milestone.emoji}
                </div>
                <div className={`text-xs font-medium ${milestone.unlocked ? 'text-emerald-700' : 'text-gray-400'
                  }`}>
                  {milestone.stage}
                </div>
                <div className="text-xs text-gray-500">
                  ${milestone.amount}+
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Pending Deposit Alerts */}
        {pendingDeposits.length > 0 && (
          <Card className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200 mb-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-8 h-8 bg-amber-400 rounded-full flex items-center justify-center">
                <Clock className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1">
                <div className="font-semibold text-amber-800">
                  {pendingDeposits.length} deposit{pendingDeposits.length > 1 ? 's' : ''} waiting for parent approval
                </div>
                <div className="text-sm text-amber-700">
                  Your parents will review these requests soon
                </div>
              </div>
            </div>

            <div className="space-y-2">
              {pendingDeposits.map((deposit) => (
                <div key={deposit.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                      <Clock className="w-4 h-4 text-amber-600" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">{deposit.description}</div>
                      <div className="text-sm text-gray-500">
                        Requested {deposit.created_at ? new Date(deposit.created_at).toLocaleDateString() : 'recently'}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-amber-600">
                      ${parseFloat(deposit.amount || 0).toFixed(2)}
                    </div>
                    <div className="text-xs text-amber-600 bg-amber-100 px-2 py-1 rounded-full">
                      Pending
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Recent Activity with Enhanced Bonus Display */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Gift className="w-5 h-5 text-emerald-600 mr-2" />
              Recent Activity
            </h3>
            {recentDeposits.length > 5 && (
              <button
                className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
                onClick={() => setShowAllActivityModal(true)}
              >
                View All
              </button>
            )}
          </div>

          {loadingDeposits ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500 mx-auto"></div>
              <div className="text-gray-500 mt-2">Loading your deposit history...</div>
            </div>
          ) : recentDeposits.length > 0 ? (
            <div className="space-y-3">
              {recentDeposits.map((deposit, index) => {
                const depositAmount = parseFloat(deposit.amount || 0);
                const bonusEarned = parseFloat(deposit.bonus_earned || 0);
                const totalValue = depositAmount + bonusEarned;

                return (
                  <div key={deposit.id || index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center relative">
                        <Plus className="w-5 h-5 text-green-600" />
                        {bonusEarned > 0 && (
                          <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-400 rounded-full flex items-center justify-center">
                            <span className="text-xs text-white font-bold">+</span>
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">
                          {deposit.description || deposit.source || 'Allowance'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {deposit.created_at ? new Date(deposit.created_at).toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric'
                          }) : deposit.verified_at ? new Date(deposit.verified_at).toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric'
                          }) : 'Recently'}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      {bonusEarned > 0 ? (
                        <div>
                          <div className="text-lg font-bold text-green-600">
                            +${totalValue.toFixed(2)}
                          </div>
                          <div className="text-xs text-yellow-600 font-medium">
                            +${bonusEarned.toFixed(2)} bonus
                          </div>
                        </div>
                      ) : (
                        <div className="text-lg font-bold text-green-600">
                          +${depositAmount.toFixed(2)}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Learning teaser at bottom */}
              <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200">
                <div className="flex items-center space-x-3">
                  <div className="text-2xl">🧠</div>
                  <div className="flex-1">
                    <div className="font-semibold text-purple-900">Want to earn bonus money?</div>
                    <div className="text-sm text-purple-700">Complete lessons to boost your savings!</div>
                  </div>
                  <Button
                    size="sm"
                    className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
                    onClick={handleStartLearning}
                  >
                    Start Learning
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <div className="text-4xl mb-4">📈</div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">No deposits yet</h4>
              <p className="text-gray-600 mb-6">Start saving money to see your deposit history here!</p>
              <div className="space-y-3">
                <Button onClick={handleRequestMoney} className={`text-emerald-700 hover:shadow-lg text-white`}>
                  <Plus className="w-4 h-4 mr-2" />
                  Request Your First Deposit
                </Button>
                <div className="text-center">
                  <span className="text-gray-500 text-sm">or </span>
                  <button
                    onClick={handleStartLearning}
                    className="text-purple-600 hover:text-purple-700 font-medium text-sm"
                  >
                    earn money by learning
                  </button>
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Goal Setting Modal */}
      <GoalSettingModal
        isOpen={showGoalModal}
        onClose={() => setShowGoalModal(false)}
        child={childData}
        currentGoal={savingsGoal}
        onGoalSet={handleGoalSet}
      />

      {/* View All Activity Modal */}
      <ViewAllActivityModal
        isOpen={showAllActivityModal}
        onClose={() => setShowAllActivityModal(false)}
        child={childData}
        initialDeposits={recentDeposits}
      />
    </div>
  );
};